import {
  pollCertificateIssuance,
  submitCertificateCsr,
  type CertificateIssuance,
} from "./ca-api";
import { generateDeviceCsr } from "./csr";
import {
  saveBrowserCertificate,
  type StoredBrowserCertificate,
} from "./browser-cert-store";

export type BrowserCertIssuePhase =
  | "generating"
  | "submitting"
  | "polling"
  | "storing";

export type IssueBrowserCertificateInput = {
  apiBase: string;
  userId: string;
  commonName: string;
  /** National code from the verified e-sign profile; the CA reads identity server-side. */
  nationalId: string;
  signal?: AbortSignal;
  onPhase?: (phase: BrowserCertIssuePhase, issuance?: CertificateIssuance) => void;
};

/**
 * Standard browser issuance: non-extractable WebCrypto key → PKCS#10 CSR → Azma CA
 * (via API proxy) → store cert PEM + CryptoKey in IndexedDB. Private key never leaves the tab.
 */
export async function issueBrowserUserCertificate(
  input: IssueBrowserCertificateInput,
): Promise<StoredBrowserCertificate> {
  if (!window.isSecureContext || !crypto.subtle) {
    throw new Error("صدور گواهی فقط روی HTTPS یا localhost ممکن است");
  }
  const cn = input.commonName.trim();
  if (cn.length < 2) {
    throw new Error("نام برای موضوع گواهی کافی نیست");
  }

  input.onPhase?.("generating");
  const { csrPem, keyPair } = await generateDeviceCsr({
    commonName: cn,
    serialNumber: input.nationalId,
    country: "IR",
  });
  if (keyPair.privateKey.extractable) {
    throw new Error("کلید خصوصی نباید قابل استخراج باشد");
  }
  if (/PRIVATE KEY/i.test(csrPem)) {
    throw new Error("کلید خصوصی نباید به سرور ارسال شود");
  }

  input.onPhase?.("submitting");
  let issuance = await submitCertificateCsr(input.apiBase, {
    csr: csrPem,
  });

  if (issuance.status !== "issued" || !issuance.certificatePem) {
    input.onPhase?.("polling", issuance);
    issuance = await pollCertificateIssuance(input.apiBase, issuance.id, {
      signal: input.signal,
      onTick: (current) => input.onPhase?.("polling", current),
    });
  }

  if (!issuance.certificatePem) {
    throw new Error("گواهی از مرکز صدور دریافت نشد");
  }

  input.onPhase?.("storing", issuance);
  const record: StoredBrowserCertificate = {
    userId: input.userId,
    issuanceId: issuance.id,
    certificatePem: issuance.certificatePem,
    chainPem: issuance.chainPem,
    privateKey: keyPair.privateKey,
    publicKey: keyPair.publicKey,
    subject: issuance.csrSubject,
    serialNumber: issuance.serialNumber,
    notBefore: issuance.notBefore,
    notAfter: issuance.notAfter,
    csrFingerprintSha256: issuance.csrFingerprintSha256,
    storedAt: new Date().toISOString(),
  };
  await saveBrowserCertificate(record);
  return record;
}
